import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
// Components
import systemModals from "components/modals/Modals";
import GenericDeleteConfirmation from "components/modals/GenericDelete";
import { AddButton, DeleteButton, ThreatApi } from "features/brm";
import { ImportThreatCatalogFileModal, ExportThreatCatalogFileModal } from "features/importer";
// Styles
import Title from "components/elements/StyledTitle";
import Buttons from "components/elements/TableButtonsDivStyled";
// Services
import { threatCatalogApi } from "services/brm/global-threat-service";
// State
import { selectedInstanceState } from "atoms/atoms-content";
import { useRecoilState } from "recoil";
import { TYPE } from "constants/brm";

import ContentLayout from "brm/layout/Content/ContentLayout";

// Local
import ThreatCatalogTable from "./tables/ThreatCatalogTable";
import ThreatCatalogAddForm from "./forms/ThreatCatalogAddForm";

function ThreatCatalog() {
  const [selectedRows, setSelectedRows] = useState([]);
  const queryClient = useQueryClient();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [uploadModalIsOpen, setUploadModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useRecoilState(selectedInstanceState);

  useEffect(() => {
    async function multiDeleteApiCalls() {
      Promise.all(selectedRows.map((each) => threatCatalogApi.deleteThreatCatalogWithHttpInfo(each.id)))
        .then(() => {
          queryClient.invalidateQueries(ThreatApi.threatCatalogKeys.all);
          setConfirmDelete(false);
          setDeleteModalIsOpen(false);
          setSelectedInstance({});
        })
        .catch((err) => {
          console.error("err in delete Promise.all : ", err.message);
        });
    }
    if (confirmDelete) {
      multiDeleteApiCalls();
    }
  }, [confirmDelete, queryClient, selectedRows, setSelectedInstance]);

  const addThreatCatalog = () => {
    setModalIsOpen(true);
  };

  const header = (
    <Title id="ThreatCatalog_title">
      Threat Catalogs
      <Buttons id="ThreatCatalog_buttons">
        {selectedRows.length > 0 && <DeleteButton onClick={() => setDeleteModalIsOpen(true)} />}
        <AddButton onClick={addThreatCatalog} />
      </Buttons>
    </Title>
  );

  const main = (
    <>
      {deleteModalIsOpen &&
        systemModals.deleteModal(
          "ThreatCatalog_deleteModal",
          deleteModalIsOpen,
          setDeleteModalIsOpen,
          <GenericDeleteConfirmation
            elementName={TYPE.threatCatalog}
            setDeleteModalIsOpen={setDeleteModalIsOpen}
            selectedRows={selectedRows}
            setConfirmDelete={setConfirmDelete}
          />,
          TYPE.threatCatalog
        )}

      {modalIsOpen &&
        systemModals.addModal(
          "ThreatCatalog_addModal",
          modalIsOpen,
          setModalIsOpen,
          <ThreatCatalogAddForm setModalIsOpen={setModalIsOpen} />,
          TYPE.threatCatalog
        )}
      <ImportThreatCatalogFileModal
        show={uploadModalIsOpen}
        threatCatalogId={selectedInstance.id}
        onHide={() => {
          setUploadModalIsOpen(false);
        }}
      />
      <ExportThreatCatalogFileModal
        show={isExportModalOpen}
        tcId={selectedInstance.id}
        tcName={selectedInstance.name}
        onHide={() => {
          setIsExportModalOpen(false);
        }}
      />

      <ThreatCatalogTable
        setSelectedRows={setSelectedRows}
        setUploadModalIsOpen={setUploadModalIsOpen}
        setIsExportModalOpen={setIsExportModalOpen}
      />
    </>
  );

  return <ContentLayout pageId="ThreatCatalog_page" header={header} main={main} />;
}

export default ThreatCatalog;
